import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import { FaTimes, FaPlay, FaCheckCircle, FaUsers } from 'react-icons/fa';
import { liveEventApi } from '../../api/scorecard';
import styles from './StartEventModal.module.css';

const StartEventModal = ({ event, programScorecard, onClose, onEventStarted }) => {
  const [enableAttendance, setEnableAttendance] = useState(false);
  const [starting, setStarting] = useState(false);
  const [error, setError] = useState(null);

  const handleStart = async () => {
    try {
      setStarting(true);
      setError(null);

      const response = await liveEventApi.startEvent(event._id, {
        enableAttendance
      });

      onEventStarted(response.event);
      onClose();
    } catch (err) {
      console.error('Error starting event:', err);
      setError(err.message || 'Failed to start event');
      setStarting(false);
    }
  };

  if (!programScorecard?.enabled) {
    return createPortal(
      <div className={styles.modalOverlay}>
        <div className={styles.modal}>
          <div className={styles.header}>
            <h2>⚠️ Scorecard Not Configured</h2>
            <button onClick={onClose} className={styles.closeButton}>
              <FaTimes />
            </button>
          </div>

          <div className={styles.content}>
            <p className={styles.errorMessage}>
              This program does not have a scorecard template configured.
            </p>
            <p className={styles.hint}>
              Please configure a scorecard template in the program settings before starting live scoring.
            </p>
          </div>

          <div className={styles.actions}>
            <button onClick={onClose} className={styles.cancelButton}>
              Close
            </button>
          </div>
        </div>
      </div>,
      document.body
    );
  }

  return createPortal(
    <div className={styles.modalOverlay}>
      <div className={styles.modal}>
        <div className={styles.header}>
          <h2><FaPlay /> Start Live Event</h2>
          <button onClick={onClose} className={styles.closeButton}>
            <FaTimes />
          </button>
        </div>

        {error && (
          <div className={styles.errorBanner}>
            ❌ {error}
          </div>
        )}

        <div className={styles.content}>
          <div className={styles.eventInfo}>
            <h3>{event.title}</h3>
            <div className={styles.infoGrid}>
              <div className={styles.infoItem}>
                <span className={styles.infoLabel}>Date:</span>
                <span className={styles.infoValue}>
                  {new Date(event.dateTime.start).toLocaleDateString()}
                </span>
              </div>
              <div className={styles.infoItem}>
                <span className={styles.infoLabel}>Time:</span>
                <span className={styles.infoValue}>
                  {new Date(event.dateTime.start).toLocaleTimeString([], { 
                    hour: '2-digit', 
                    minute: '2-digit' 
                  })}
                </span>
              </div>
              {event.teams?.home?.name && (
                <>
                  <div className={styles.infoItem}>
                    <span className={styles.infoLabel}>Home:</span>
                    <span className={styles.infoValue}>{event.teams.home.name}</span>
                  </div>
                  <div className={styles.infoItem}>
                    <span className={styles.infoLabel}>Away:</span>
                    <span className={styles.infoValue}>{event.teams.away.name}</span>
                  </div>
                </>
              )}
            </div>
          </div>

          <div className={styles.scorecardInfo}>
            <h4>Scorecard Template</h4>
            <div className={styles.templateBadge}>
              <span className={styles.templateName}>{programScorecard.templateName}</span>
              {programScorecard.isLocked && (
                <span className={styles.lockedBadge}>🔒 Locked</span>
              )}
            </div>
            <p className={styles.templateDescription}>
              This scorecard will be used to track scores and statistics during the event.
            </p>
          </div>

          <div className={styles.options}>
            <h4>Options</h4>
            <label className={styles.checkboxLabel}>
              <input
                type="checkbox"
                checked={enableAttendance}
                onChange={(e) => setEnableAttendance(e.target.checked)}
                className={styles.checkbox}
              />
              <div>
                <div className={styles.optionTitle}>
                  <FaUsers /> Enable Attendance Tracking
                </div>
                <div className={styles.optionDescription}>
                  Track which participants attended this event. Attendance will be pre-populated from RSVPs.
                </div>
              </div>
            </label>
          </div>

          <div className={styles.notice}>
            <FaCheckCircle />
            <div>
              <strong>Ready to start?</strong>
              <p>Once started, the event will be marked as "Live" and scoring will begin. You can update scores, statistics, and log events in real-time.</p>
            </div>
          </div>
        </div>

        <div className={styles.actions}>
          <button 
            onClick={handleStart}
            className={styles.startButton}
            disabled={starting}
          >
            {starting ? 'Starting...' : 'Start Event'}
          </button>
          <button 
            onClick={onClose}
            className={styles.cancelButton}
            disabled={starting}
          >
            Cancel
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default StartEventModal;

